import React from "react";
import { journey, brand } from "../mock";
import { Button } from "../components/ui/button";
import { AnimateOnScroll } from "../hooks/useScrollAnimation";
import { Compass, Search, Wrench, ShieldCheck, Check, ArrowRight } from "lucide-react";
import { SectionHeading } from "./SectionHeading";

const iconMap = { Compass, Search, Wrench, ShieldCheck };

function prefillContact(name) {
  try {
    window.__setContactSubjectPreset?.(name);
  } catch (e) {}
  const el = document.getElementById("kontakt");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

function PackageCard({ pkg, accent, dark }) {
  const isPrimary = !!pkg.primary;
  return (
    <div
      data-testid={`package-card-${pkg.id}`}
      className={`group relative flex h-full flex-col overflow-hidden rounded-2xl border bg-white p-7 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_60px_rgba(2,6,23,0.10)] ${
        isPrimary ? "border-cyan-200" : "border-slate-200/80 hover:border-[#28D8FF]/50"
      }`}
    >
      {/* Top accent bar */}
      <span
        className="absolute inset-x-0 top-0 h-1"
        style={{ background: isPrimary ? accent : `linear-gradient(90deg, ${dark}, rgba(0,45,114,0.25))` }}
        aria-hidden
      />

      <div className="flex items-center justify-between gap-2">
        <div className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em]" style={{ color: dark }}>
          {pkg.tagline}
        </div>
        <div className="flex items-center gap-1.5">
          {pkg.recommended ? (
            <span className="rounded-full px-2.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wider text-white" style={{ backgroundColor: dark }}>
              Empfohlen
            </span>
          ) : null}
          {pkg.monthly ? (
            <span className="rounded-full border border-cyan-200 bg-cyan-50 px-2.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wider text-slate-700">
              Monatlich
            </span>
          ) : null}
        </div>
      </div>

      <h4 className="mt-2 font-display text-xl font-bold text-[#0A111F]">{pkg.name}</h4>

      <ul className="mt-4 space-y-2.5 text-sm text-slate-700">
        {pkg.bullets.map((b, i) => (
          <li key={i} className="flex items-start gap-2.5">
            <span
              className="mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded-full"
              style={{ backgroundColor: isPrimary ? accent : "rgba(40,216,255,0.18)" }}
            >
              <Check size={11} strokeWidth={3} style={{ color: isPrimary ? "#063247" : dark }} />
            </span>
            <span>{b}</span>
          </li>
        ))}
      </ul>

      <div className="mt-auto flex items-center justify-between gap-3 pt-7">
        <span className="font-mono text-[11px] font-medium uppercase tracking-wider text-slate-500">
          {isPrimary ? "Kostenlos" : "Auf Anfrage"}
        </span>
        <Button
          data-testid={`package-cta-${pkg.id}`}
          onClick={() => prefillContact(pkg.name)}
          className={`rounded-full text-sm font-semibold ${isPrimary ? "" : "border bg-white hover:bg-slate-50"}`}
          style={
            isPrimary
              ? { backgroundColor: accent, color: "#063247" }
              : { borderColor: dark, color: dark }
          }
        >
          {pkg.cta}
          <ArrowRight size={15} className="ml-1.5 transition-transform group-hover:translate-x-0.5" />
        </Button>
      </div>
    </div>
  );
}

export default function PackagesSection() {
  const accent = brand.colors.cyan;
  const dark = brand.colors.navy;

  return (
    <section id="angebote" data-testid="packages-section" className="scroll-mt-24 border-y border-slate-200/70 bg-[#F4F6F8]">
      <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
        <AnimateOnScroll animation="fade-up">
          <SectionHeading
            overline="Unser Vorgehen"
            title="Dein Weg zu mehr Sicherheit"
            kicker="Klarer Ablauf statt reiner Stunden: vom unverbindlichen Einstieg über die Analyse und technische Umsetzung bis zur laufenden Betreuung."
          />
        </AnimateOnScroll>

        <div className="relative mt-16">
          {/* Vertical connecting path (desktop) */}
          <div
            className="absolute left-[27px] top-6 bottom-10 hidden w-px md:block"
            style={{ background: `linear-gradient(to bottom, ${accent}, rgba(0,45,114,0.15))` }}
            aria-hidden
          />

          <div className="space-y-14 md:space-y-20">
            {journey.map((stage, idx) => {
              const StageIcon = iconMap[stage.icon] || Compass;
              return (
                <AnimateOnScroll key={stage.step} animation="fade-up" delay={idx * 80}>
                  <div className="relative md:pl-20">
                    {/* Step node (desktop) */}
                    <div
                      className="absolute left-0 top-0 hidden h-14 w-14 place-items-center rounded-2xl text-white shadow-[0_8px_24px_rgba(0,45,114,0.35)] md:grid"
                      style={{ background: "linear-gradient(135deg, #0A111F, #002D72 130%)" }}
                    >
                      <span className="font-mono text-sm font-bold" style={{ color: accent }}>
                        {stage.step}
                      </span>
                    </div>

                    {/* Stage header */}
                    <div className="mb-6">
                      <div className="flex items-center gap-2.5">
                        <span
                          className="grid h-7 w-7 place-items-center rounded-lg md:hidden"
                          style={{ backgroundColor: dark }}
                        >
                          <span className="font-mono text-xs font-bold" style={{ color: accent }}>{stage.step}</span>
                        </span>
                        <StageIcon size={17} style={{ color: dark }} />
                        <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">
                          {stage.stage}
                        </span>
                      </div>
                      <h3 className="mt-2.5 font-display text-2xl font-bold text-[#0A111F] md:text-3xl">{stage.title}</h3>
                      <p className="mt-2 max-w-2xl text-slate-600">{stage.description}</p>
                    </div>

                    {/* Package cards */}
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:gap-6">
                      {stage.packages.map((pkg) => (
                        <PackageCard key={pkg.name} pkg={pkg} accent={accent} dark={dark} />
                      ))}
                    </div>
                  </div>
                </AnimateOnScroll>
              );
            })}
          </div>
        </div>

        {/* Closing CTA */}
        <AnimateOnScroll animation="fade-up">
          <div className="relative mt-20 overflow-hidden rounded-2xl p-10 text-center shadow-[0_24px_60px_rgba(0,45,114,0.25)] md:p-14" style={{ background: "linear-gradient(135deg, #0A111F 0%, #002D72 140%)" }}>
            <div className="pointer-events-none absolute inset-0 hero-grid opacity-60" aria-hidden />
            <div className="pointer-events-none absolute -top-16 -right-10 h-56 w-56 rounded-full" style={{ background: "radial-gradient(circle, rgba(40,216,255,0.3), rgba(40,216,255,0) 70%)" }} />
            <div className="relative">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3.5 py-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-white backdrop-blur">
                <ShieldCheck size={14} style={{ color: accent }} /> Unverbindlich starten
              </span>
              <h3 className="mt-5 font-display text-2xl font-bold text-white md:text-4xl">
                Nicht sicher, wo du starten solltest?
              </h3>
              <p className="mx-auto mt-3 max-w-xl text-slate-300">
                Im kostenlosen Erstgespräch finden wir gemeinsam den passenden Einstieg für deine Umgebung.
              </p>
              <div className="mt-8">
                <Button
                  data-testid="packages-closing-cta"
                  onClick={() => prefillContact("Kostenloses Erstgespräch")}
                  className="group max-w-full whitespace-normal rounded-full px-5 py-4 text-sm font-semibold leading-tight shadow-[0_0_15px_rgba(40,216,255,0.2)] transition-shadow hover:shadow-[0_0_28px_rgba(40,216,255,0.5)] sm:px-7 sm:py-6 sm:text-base"
                  style={{ backgroundColor: accent, color: "#063247" }}
                >
                  Kostenloses Erstgespräch vereinbaren
                  <ArrowRight size={17} className="ml-2 shrink-0 transition-transform group-hover:translate-x-0.5" />
                </Button>
              </div>
            </div>
          </div>
        </AnimateOnScroll>
      </div>
    </section>
  );
}
